import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { WifiOff } from 'lucide-react'
import { useState } from 'react'

import { BillingProfileCard } from './BillingProfileCard'
import { PaymentHistory } from './PaymentHistory'
import { PlanCard } from './PlanCard'
import { SubscriptionStatusBanner } from './SubscriptionStatusBanner'
import type { BillingOverview, Plan } from './types'
import { formatDate, formatSoles } from './types'
import { ApiException, api } from '../../api/client'
import type { TenantUsage } from '../../api/types.gen'
import { formatBytes } from '../../lib/format'
import { usePageTitle } from '../../lib/usePageTitle'
import { Banner, Button, Card, ErrorState, Progress, Skeleton } from '../../ui'

/**
 * Plan y uso.
 *
 * Una sola pantalla para «cuánto ocupo» y «cuánto pago» porque son la misma
 * pregunta: el precio se mide en almacenamiento. Separarlas obligaría a ir y
 * venir para decidir si conviene cambiar de plan.
 */
export function UsagePage() {
  usePageTitle('Plan y uso')
  const queryClient = useQueryClient()
  const [notice, setNotice] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const overview = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => api.get<{ data: BillingOverview }>('/billing/subscription'),
  })

  const plans = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: () => api.get<{ data: Plan[] }>('/billing/plans'),
  })

  /*
   | El almacenamiento sale de aquí y no del resumen de facturación, aunque
   | aquél también trae un número de uso.
   |
   | El del resumen es lo ALMACENADO, que es lo que hay que comparar contra el
   | plan al bajarse de plan. El de aquí incluye además lo que está subiendo en
   | este momento, que es contra lo que el servidor decide si cabe un archivo
   | más. Enseñar el primero es el fallo que ya advierte el comentario de abajo
   | sobre las derivadas: el estudio ve 190 GB y se le bloquea a los 200.
   |
   | Es la misma consulta que pinta la barra de la pantalla de proyecto, así
   | que las dos no pueden discrepar.
   */
  const usage = useQuery({
    queryKey: ['tenant-usage'],
    queryFn: () => api.get<{ data: TenantUsage | null }>('/tenant/usage'),
  })

  const changePlan = useMutation({
    mutationFn: (code: string) =>
      api.post<{ data: { applied: 'now' | 'period_end'; plan: string; charged_cents: number } }>(
        '/billing/subscription/plan',
        { plan: code },
      ),
    onSuccess: (result) => {
      setFailure(null)
      setNotice(
        result.data.applied === 'now'
          ? `Plan actualizado. Se cobró ${formatSoles(result.data.charged_cents)} por lo que queda del mes.`
          : 'El cambio se aplicará al final del periodo que ya has pagado.',
      )
      void queryClient.invalidateQueries({ queryKey: ['billing'] })
    },
    // El backend manda mensajes ya redactados para el estudio —cuánto espacio
    // liberar, cuántos usuarios sobran—, así que se muestran tal cual en vez de
    // traducir códigos aquí y tener el texto en dos sitios.
    onError: (error) => {
      setNotice(null)
      setFailure(error instanceof ApiException ? error.error.message : 'No se pudo cambiar el plan.')
    },
  })

  const cancel = useMutation({
    mutationFn: (value: boolean) =>
      api.post<{ data: unknown }>('/billing/subscription/cancel', { cancel: value }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['billing'] }),
  })

  if (overview.isPending || plans.isPending) {
    return (
      <section className="flex max-w-page flex-col gap-6">
        <Skeleton className="h-9 w-48" />
        <Skeleton className="h-32 w-full rounded-card" />
        <Skeleton className="h-48 w-full rounded-card" />
      </section>
    )
  }

  if (overview.error || plans.error) {
    return (
      <ErrorState
        icon={WifiOff}
        title="No se pudo cargar la información de tu plan"
        description="Casi siempre es la conexión. Vuelve a intentarlo."
        action={
          <Button
            variant="secondary"
            onClick={() => {
              void overview.refetch()
              void plans.refetch()
            }}
          >
            Reintentar
          </Button>
        }
      />
    )
  }

  const { subscription, plan, pending_plan, usage: cuentas, billing_profile } = overview.data.data
  const almacenamiento = usage.data?.data ?? null
  const limit = almacenamiento?.limit_bytes ?? plan?.max_storage_bytes ?? 0
  const usados = almacenamiento?.used_bytes ?? 0
  const percent = limit > 0 ? Math.min(100, Math.round((usados / limit) * 100)) : 0

  return (
    <section className="flex max-w-page flex-col gap-6">
      <h1 className="text-title font-semibold text-ink">Plan y uso</h1>

      <SubscriptionStatusBanner subscription={subscription} />

      {/* Uso */}
      <Card className="p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-subhead font-medium text-ink">Almacenamiento</h2>
          <p className="text-body text-ink-muted" data-numeric>
            {formatBytes(usados)} de {formatBytes(limit)}
          </p>
        </div>

        <Progress
          className="mt-3"
          value={percent}
          label="Almacenamiento usado"
          warnAt={75}
          dangerAt={90}
        />

        {/* El desglose, y por qué la barra no lo lleva todo dentro.
            ─────────────────────────────────────────────────────────────────
            La regla es la que HACE CUMPLIR el servidor, no la que se suponía.
            `QuotaGuard::snapshot()` mide `total_bytes + inflight` y nada más:
            las derivadas y la papelera viven en columnas aparte y NO cuentan.

            Hasta el 09-09-2026 aquí ponía «Incluye miniaturas y
            previsualizaciones», que era falso y en la dirección peor: le
            decíamos al estudio que le cobrábamos algo que no le cobramos.

            Y hace falta enseñarlo, no sólo dejar de mentir. Un estudio que
            sube 152 GB y ve 152 GB no tiene pregunta; pero el que mira su
            cuenta y encuentra 187 GB en R2 sí, y hoy no hay dónde responderla. */}
        <dl className="mt-5 grid gap-x-6 gap-y-2 border-t border-line pt-4 sm:grid-cols-2">
          <Linea
            etiqueta="Tus archivos"
            valor={formatBytes(almacenamiento?.stored_bytes ?? usados)}
            nota={`${(almacenamiento?.total_files ?? 0).toLocaleString('es-PE')} archivos`}
          />
          {(almacenamiento?.inflight_bytes ?? 0) > 0 && (
            <Linea
              etiqueta="Subiendo ahora"
              valor={formatBytes(almacenamiento?.inflight_bytes ?? 0)}
              nota="reservado hasta que termine"
            />
          )}
          <Linea
            etiqueta="Miniaturas y previas"
            valor={formatBytes(almacenamiento?.rendition_bytes ?? 0)}
            nota="no consume cuota"
          />
          <Linea
            etiqueta="En la papelera"
            valor={formatBytes(almacenamiento?.trashed_bytes ?? 0)}
            nota="no consume cuota"
          />
        </dl>

        <p className="mt-4 text-caption text-ink-subtle">
          Las descargas de tus clientes no consumen cuota ni se cobran nunca.
          {almacenamiento?.last_reconciled_at != null && (
            <>
              {' '}
              Comprobado contra el almacén el {formatDate(almacenamiento.last_reconciled_at)}.
            </>
          )}
        </p>

        <p className="mt-4 text-body text-ink-muted" data-numeric>
          {cuentas.users} de {plan?.max_users ?? 0} {plan?.max_users === 1 ? 'usuario' : 'usuarios'}
        </p>
      </Card>

      {/* Suscripción */}
      <Card className="p-5">
        <h2 className="text-subhead font-medium text-ink">Suscripción</h2>

        <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2">
          <Row label="Plan actual" value={plan?.name ?? '—'} />
          <Row
            label="Importe"
            value={plan ? `${formatSoles(plan.total_cents)} al mes (IGV incluido)` : '—'}
          />
          <Row
            label={subscription.cancel_at_period_end ? 'Termina el' : 'Próximo cobro'}
            value={formatDate(
              subscription.cancel_at_period_end
                ? subscription.current_period_end
                : subscription.next_charge_at,
            )}
          />
          <Row
            label="Tarjeta"
            value={
              subscription.card?.last4
                ? `${subscription.card.brand ?? 'Tarjeta'} •••• ${subscription.card.last4}`
                : 'Sin tarjeta'
            }
          />
        </dl>

        {pending_plan && (
          <p className="mt-4 rounded-control bg-surface-sunken px-3 py-2 text-body text-ink-muted">
            A partir del {formatDate(subscription.current_period_end)} pasarás a{' '}
            <strong className="font-medium text-ink">{pending_plan.name}</strong>.
          </p>
        )}

        <button
          onClick={() => cancel.mutate(!subscription.cancel_at_period_end)}
          disabled={cancel.isPending}
          className="action mt-4 text-label text-ink-subtle underline underline-offset-4 transition-colors hover:text-ink disabled:opacity-50"
        >
          {subscription.cancel_at_period_end ? 'Reactivar la suscripción' : 'Cancelar la suscripción'}
        </button>
      </Card>

      {notice && (
        <Banner tone="positive" live="status">
          {notice}
        </Banner>
      )}
      {failure && (
        <Banner tone="danger" live="alert">
          {failure}
        </Banner>
      )}

      {/* Planes */}
      <div className="flex flex-col gap-3">
        <h2 className="text-subhead font-medium text-ink">Cambiar de plan</h2>

        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {plans.data.data.map((candidate) => (
            <li key={candidate.id}>
              <PlanCard
                plan={candidate}
                current={candidate.id === plan?.id}
                pending={candidate.id === pending_plan?.id}
                busy={changePlan.isPending}
                onChoose={() => changePlan.mutate(candidate.code)}
              />
            </li>
          ))}
        </ul>

        <p className="text-caption text-ink-subtle">
          Subir de plan es inmediato y sólo se cobra la diferencia de los días que quedan. Bajar se
          aplica al final del periodo que ya has pagado.
        </p>
      </div>

      {/* Lo que ya se pagó va después de lo que se puede cambiar: se entra aquí
          a decidir el plan mucho más a menudo que a revisar un cargo. */}
      <PaymentHistory />

      <BillingProfileCard profile={billing_profile} />
    </section>
  )
}

/**
 * Una línea del desglose: cuánto, y si cuenta o no.
 *
 * La `nota` no es decoración. «no consume cuota» es la mitad del mensaje: sin
 * ella, ver 12 GB en la papelera debajo de una barra al 76% se lee como «12 de
 * esos 76 son basura», que es justo lo contrario de lo que pasa.
 */
function Linea({ etiqueta, valor, nota }: { etiqueta: string; valor: string; nota: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-body text-ink-muted">{etiqueta}</dt>
      <dd className="text-right">
        <span className="text-body font-medium text-ink" data-numeric>
          {valor}
        </span>
        <span className="ml-2 text-caption text-ink-subtle">{nota}</span>
      </dd>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-caption text-ink-subtle">{label}</dt>
      <dd className="text-body font-medium text-ink" data-numeric>
        {value}
      </dd>
    </div>
  )
}
